/**
 * connectionNote — one note per connection.
 *
 * Note and Nickname were two fields on the same row: `connection_views.nickname`
 * (the "how I know them" descriptor) and `connection_views.notes` (the older
 * private note, which also dual-writes to `connection_data.notes`). The
 * 2026-08-18 Detail frames show ONE field, so `nickname` is the survivor and
 * `notes` drains into it on read.
 *
 * Both columns are DB-backed, so nothing here is rescuing unrecoverable text —
 * this is a migration, not a repair. It has to be idempotent: mount paints the
 * cache, then the DB refresh lands, so the resolve runs more than once per view
 * and must never append the same note twice.
 */

// Two sentences from two fields, joined so neither is lost. Only reachable for
// a connection that carried both — no row in dev had both set.
export const NOTE_JOIN = ' — ';

/**
 * Resolve the single note for a connection.
 *
 * @param {object} source - { nickname, notes } as stored on the connection
 * @returns {{ text: string, needsMigration: boolean }}
 *   text - what the one field shows
 *   needsMigration - the legacy note still holds text and should be drained
 *     into nickname and then cleared. False once `notes` is empty, which is
 *     what makes a second pass a no-op.
 */
/**
 * Legacy stored notes are not reliably strings.
 *
 * `connection_data.notes` has been written by several code versions across the
 * life of the app, and real devices hold BOTH shapes: a bare string, and an
 * older `{ text, ... }` object. The list page's search carried a
 * `c.notes?.text` clause for exactly this; it was removed on 2026-08-20 as
 * "dead" on the evidence that every CURRENT writer stores a string — which was
 * true, and irrelevant. The writers are not the data. 329 connections on one
 * real device crashed the People page on the first render after that removal
 * (`(t || '').trim is not a function`).
 *
 * So: read the shape that is actually there. An unexpected shape returns empty
 * rather than throwing — a note is not worth a white screen — but it warns, so
 * a third shape surfaces as a log instead of hiding behind a coercion.
 */
const noteText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && typeof value.text === 'string') return value.text;
  console.warn('[CONNECTION_NOTE] Unexpected note shape, ignoring:', value);
  return '';
};

export const resolveConnectionNote = ({ nickname, notes } = {}) => {
  const nick = noteText(nickname).trim();
  const note = noteText(notes).trim();

  // Nothing legacy left — the steady state after a migration has run.
  if (!note) return { text: nick, needsMigration: false };

  // Note only: it becomes the nickname verbatim.
  if (!nick) return { text: note, needsMigration: true };

  // Already merged but the clear didn't land (write succeeded, clear failed).
  // Re-appending here is exactly the double-text bug, so treat it as done.
  if (nick === note || nick.includes(note)) return { text: nick, needsMigration: true };

  // Genuinely different text in both — keep both, nickname first.
  return { text: `${nick}${NOTE_JOIN}${note}`, needsMigration: true };
};
